// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import TimeLineChart, {
  TimeLineData,
} from 'components/molecules/TimeLineChart';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createAxios } from 'ts/createAxios';
import { DateTypeRange } from 'util/Enums';
import { mergeClasses } from 'util/commons';
import { API_ENDPOINTS } from 'util/endPoints';
import { handleError } from 'util/errorHandler';
import { PoolDashboardMasterFilter } from '../PoolDashboardFilter';
import styles from './PoolDashboardLineCharts.module.scss';
import { useCallbackLoading } from 'util/hooks/useCallbackLoading';
import LoadingCoating from 'components/atoms/LoadingCoating';

type PoolDashboardLineChartsProps = {
  filter?: PoolDashboardMasterFilter;
  className?: string;
  fetchTrigger?: number;
};

type WasteTrend = {
  date: string;
  weight: string;
};

const PoolDashboardLineCharts: React.FC<PoolDashboardLineChartsProps> = ({
  filter,
  className,
  fetchTrigger,
}) => {
  const { t } = useTranslation();
  const [data, setData] = useState<WasteTrend[]>([]);
  const [dateType, setDateType] = useState<DateTypeRange>(DateTypeRange.MONTH);

  const { triggerFetch, loading } = useCallbackLoading({
    callback: (p: {
      dateType: DateTypeRange;
      filter?: PoolDashboardMasterFilter;
    }) =>
      createAxios({
        notShowLoading: true,
      })
        .post(API_ENDPOINTS.DASHBOARDS_POOL_WASTE_TREND, {
          ...p.filter,
          typeDateRange: p.dateType,
        })
        .then(({ data }) => setData(data))
        .catch((err) => {
          handleError(err);
          setData([]);
        }),
  });

  useEffect(() => {
    triggerFetch({
      filter,
      dateType,
    });
  }, [filter, dateType, fetchTrigger]);

  const convertedData = useMemo(
    (): TimeLineData[] =>
      data.map(({ date, weight }) => ({
        label: date,
        value: Number(weight),
        isPredictive: dayjs().isBefore(date, dateType),
      })),
    [data]
  );

  return (
    <div className={mergeClasses(styles.PoolDashboardLineCharts, className)}>
      <LoadingCoating
        show={loading}
        styles={{
          borderRadius: 12,
        }}
      />

      <TimeLineChart
        title={t('pool_dashboard.changes_in_generated_weight')}
        noteMessage={t('pool_dashboard.line_chart_note_message') || ''}
        data={convertedData}
        height={284}
        filterType={dateType}
        onFilterTypeChange={setDateType}
        unit="kg"
        legends={{
          normal: t('pool_dashboard.solid_line') || '',
          predictive: t('pool_dashboard.dashed_line') || '',
        }}
      />
    </div>
  );
};

export default PoolDashboardLineCharts;
